import { queryOptions } from '@tanstack/react-query';
import { createServerFn } from '@tanstack/react-start';
import { getCookie } from '@tanstack/react-start/server';

import { queryKeys } from '@/hooks/api/query-keys';
import { SESSION_COOKIE_NAME } from '@/utils/session/constants';
import { dashboardFromSession } from '@/utils/session/dashboard-from-session';
import {
  getPassportUserFromSession,
  getSessionFromRedis,
} from '@/utils/session/get-session-from-redis';
import { parseSessionCookie } from '@/utils/session/parse-cookie';

import { evaluateFeatureFlag } from './server/evaluate-feature-flag';

/**
 * Whether the signed-in researcher gets settings in this app rather than the
 * legacy website. Requires the researcher V3 experience as well as the flag:
 * settings pages are only reachable from inside it. Reads the session from
 * Redis instead of taking a user from the caller, and fails closed, so an
 * unconfigured PostHog (local dev without `POSTHOG_API_KEY`) or an outage
 * sends everyone to the legacy pages.
 */
export const isSettingsV3Enabled = createServerFn().handler(
  async (): Promise<boolean> => {
    try {
      const cookie = getCookie(SESSION_COOKIE_NAME);
      const sessionId = cookie ? parseSessionCookie(cookie) : null;
      if (!sessionId) return false;

      const sessionData = await getSessionFromRedis(sessionId);
      if (!sessionData?.v3Enabled) return false;

      const passportUser = getPassportUserFromSession(sessionData);
      if (!passportUser) return false;

      const { organization, team } = dashboardFromSession({
        sessionState: passportUser.sessionState,
      });
      return await evaluateFeatureFlag('settings-v3', passportUser.id, {
        organizationId: organization ?? undefined,
        teamId: team ?? undefined,
      });
    } catch (error) {
      console.error(
        '[feature-flags] Error evaluating flag "settings-v3"',
        error,
      );
      return false;
    }
  },
);

/**
 * Keyed by the active workspace, since the flag can target an organization or
 * team: switching either re-evaluates it. The route gates and the account
 * menu share this entry, so the menu never disagrees with the gate.
 */
export function settingsV3QueryOptions(
  userId: string,
  organizationId: string | null,
  teamId: string | null,
) {
  return queryOptions({
    queryKey: queryKeys.featureFlags.settingsV3(userId, organizationId, teamId),
    queryFn: () => isSettingsV3Enabled(),
    staleTime: 60_000,
  });
}
